/**
 * DepthChartAgent — Football/Flag: assign players in formations.
 * Baseball/Softball: save pitching, batting order, and defensive slots on a **game** (game_fielding / game_lineup).
 * Other sports: route coaches to Depth Chart for roster-style positions.
 */

import { assignPlayerToFormation } from '../../../services/depthChartAssignmentService';
import { isAssignPlayerToFormationIntent, isGameLineupCoachIntent } from './intents';
import {
  extractAssignFormationFromMessage,
  extractAssignPlayerPositionOnly,
} from './parsers';
import {
  fetchRosterForInjury,
  findPlayersForInjury,
  playerRefToIdentifier,
} from './playerLookup';
import type { AgentRunContext, AgentRunResult, CoachSteveAgent } from './types';
import {
  handleBaseballSoftballDepthRequest,
  handleDepthChartCoachMessageForTeamSport,
} from './depthChartGameScoped';

export const depthChartAgent: CoachSteveAgent = {
  name: 'depthChart',
  description:
    'Football / Flag Football: assigns players inside formations on the Depth Chart. Baseball / Softball: saves pitching, batting order, and defensive assignments on the correct **game**. Other sports: use Depth Chart for starters and rotations.',
  applicableSports: 'all',
  match: (text) => isAssignPlayerToFormationIntent(text) || isGameLineupCoachIntent(text),
  async handle(text, ctx: AgentRunContext): Promise<AgentRunResult> {
    const sport = ctx.team.sport;

    if (sport === 'Baseball' || sport === 'Softball') {
      return handleBaseballSoftballDepthRequest(text, ctx);
    }

    if (sport === 'Football' || sport === 'FlagFootball') {
      const parsedWithFormation = extractAssignFormationFromMessage(text);
      if (!parsedWithFormation) {
        const parsedPositionOnly = extractAssignPlayerPositionOnly(text);
        if (parsedPositionOnly) {
          ctx.addMessage(
            'assistant',
            `I can assign **${parsedPositionOnly.playerRef}** to **${parsedPositionOnly.positionKey}**. Tell me which formation — e.g. **assign ${parsedPositionOnly.playerRef} to ${parsedPositionOnly.positionKey} in Spread** (offense) or **in 4-3** (defense).`
          );
          return { kind: 'needs_input' };
        }
        if (isGameLineupCoachIntent(text) && !isAssignPlayerToFormationIntent(text)) {
          return handleDepthChartCoachMessageForTeamSport(text, ctx);
        }
        ctx.addMessage(
          'assistant',
          "I couldn't parse that. Use: **put [player] at [position] in [formation]** — e.g. \"put John at QB in Spread\" or \"assign #5 to RB in the Spread formation\"."
        );
        return { kind: 'handled' };
      }

      ctx.setIsLoading(true);
      try {
        const roster = await fetchRosterForInjury(ctx.team.id);
        const identifier = playerRefToIdentifier(parsedWithFormation.playerRef);
        const matches = findPlayersForInjury(roster, identifier);
        if (matches.length === 0) {
          ctx.addMessage(
            'assistant',
            "I don't see that player on the roster. Use their name or jersey number (e.g. \"put John at QB in Spread\" or \"assign #5 to RB in Spread\")."
          );
          return { kind: 'handled' };
        }
        if (matches.length > 1) {
          const list = matches
            .map((p) => `**${p.first_name} ${p.last_name}**${p.number != null ? ` #${p.number}` : ''}`)
            .join(', ');
          ctx.addMessage(
            'assistant',
            `I found more than one: ${list}. Which one? (Use jersey number, e.g. assign #5 to RB in Spread.)`
          );
          return { kind: 'needs_input' };
        }
        const player = matches[0];
        const result = await assignPlayerToFormation(
          ctx.team.id,
          sport,
          parsedWithFormation.formationName,
          parsedWithFormation.positionKey,
          player.id,
          1
        );
        if (!result.success) {
          ctx.addMessage(
            'assistant',
            result.error ||
              "Couldn't update the depth chart. Check that the **formation** and **position** exist (e.g. Spread has QB, RB; 4-3 has LDE, LDT, MLB). You can set formations in **Depth Chart**."
          );
          return { kind: 'handled' };
        }
        await ctx.refreshTeamContextForAgent();
        const name = `${player.first_name} ${player.last_name}`.trim();
        const num = player.number != null ? ` #${player.number}` : '';
        if (result.previousPosition) {
          ctx.addMessage(
            'assistant',
            `Moved **${name}**${num} from **${result.previousPosition}** to **${parsedWithFormation.positionKey}** in the **${parsedWithFormation.formationName}** formation. A player can only be in one position per formation — this has been updated.`,
            { label: 'Open Depth Chart', href: '/depth-chart' }
          );
        } else {
          ctx.addMessage(
            'assistant',
            `Done — **${name}**${num} is now at **${parsedWithFormation.positionKey}** in the **${parsedWithFormation.formationName}** formation.`,
            { label: 'Open Depth Chart', href: '/depth-chart' }
          );
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('depth-chart-updated', { detail: { teamId: ctx.team.id } })
          );
        }
        return { kind: 'handled' };
      } catch (err) {
        console.error(err);
        ctx.addMessage(
          'assistant',
          "Something went wrong updating the depth chart. Try **Depth Chart** in the app to assign players manually."
        );
        return { kind: 'error', error: String(err) };
      } finally {
        ctx.setIsLoading(false);
      }
    }

    return handleDepthChartCoachMessageForTeamSport(text, ctx);
  },
};
